import {
  Component,
  ElementRef,
  Input,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { FolderService } from '../../../folder.service';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { AlertService } from '@core/services/alert.service';
import { Store } from '@ngxs/store';
import {
  FolderDetail,
  FolderItemList,
  FolderParams,
  FolderPermissionChoices,
} from '../../../folder.model';
import {
  DeviceType,
  Nullable,
  Pagination,
} from '@shared/models/common.model';
import {
  ListViewToggleComponent,
  ViewType,
} from '@shared/components/list-view-toggle/list-view-toggle.component';
import { DrfList } from '@shared/models/drf-response.models';
import { Params, Router } from '@angular/router';
import { FolderPathsComponent } from '../../folder-paths/folder-paths.component';
import {
  CardDetailDirective,
  CardThumbnailDirective,
  GridViewCardComponent,
} from '@shared/components/grid-view-card/grid-view-card.component';
import { FileIconComponent } from '@shared/components/file-icon/file-icon.component';
import { SharedModule } from '@shared/shared.module';
import { TableModule } from 'primeng/table';
import { ErrorNotification } from '../../../../../store/memo/memo.actions';
import { SpinnerService } from '@core/services/spinner.service';
import { MemoService } from '../../../../memos/service/memo.service';
import { ThemeService } from '@shared/service/theme.service';
// eslint-disable-next-line max-len
import { ModalConfirmationStdMobileHeaderComponent } from '@shared/components/modal-confirmation-std-mobile-header/modal-confirmation-std-mobile-header.component';
import { FolderShareWithDisplayComponent } from '../../folder-share-with-display/folder-share-with-display.component';
import { finalize } from 'rxjs';
import { UrlToExtensionPipe } from '@shared/pipes/url-to-extension.pipe';

@Component({
  selector: 'app-move-to-folder',
  standalone: true,
  templateUrl: './move-to-folder.component.html',
  styleUrls: ['./move-to-folder.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    ListViewToggleComponent,
    FolderPathsComponent,
    CardDetailDirective,
    FileIconComponent,
    GridViewCardComponent,
    SharedModule,
    TableModule,
    CardThumbnailDirective,
    ModalConfirmationStdMobileHeaderComponent,
    FolderShareWithDisplayComponent,
    UrlToExtensionPipe,
  ],
})
export class MoveToFolderComponent {
  @Input() mode: 'moveToFolder' | 'addToFolder';
  @Input() formerLocation: FolderDetail | undefined | null;
  @Input() filterWithPermission: FolderPermissionChoices;
  @Input() itemId: number; // moveToFolder mode -> itemId = FolderItemId, addToFolder -> itemId = MemoId
  @Input() disabledFolders: number[] = [];
  @Input() buttonTitle = 'FOLDERS.MOVE-TO-FOLDER.MOVE-TO';
  @Input() buttonIcon = '/assets/images/icons/files/move.svg#move';
  @Input() customBody = false;

  @ViewChild('modalTemp', { static: true })
  modalTemp: ElementRef;

  modal: NgbModalRef;
  filters: Nullable<FolderParams> = {};
  pagination: Pagination = {
    page: 1,
    page_size: 20,
  };
  layOutView: ViewType = 'grid_view';
  loading = false;

  selectedFolder: FolderItemList | undefined;

  folderItemRes: DrfList<FolderItemList> | undefined;
  backupList: FolderItemList[] = [];
  currentFolderDetail: FolderDetail | undefined;

  constructor(
    protected service: FolderService,
    private modalService: NgbModal,
    private alert: AlertService,
    private spinner: SpinnerService,
    private store: Store,
    private router: Router,
    private memoService: MemoService,
    private themeService: ThemeService,
  ) {}

  openModal() {
    if (this.modal) {
      this.modal.close();
    }
    this.onFilterChange();
    this.modal = this.modalService.open(this.modalTemp, {
      centered: true,
      size: 'lg',
      backdrop: this.deviceType !== 'mobile',
      windowClass:
        this.deviceType === 'mobile' ? 'full-device-content' : '',
    });
  }

  onClickFolderItem(folderItem: FolderItemList) {
    if (
      folderItem.type === 'folder' &&
      folderItem.id !== this.itemId
    ) {
      if (this.selectedFolder?.id == folderItem.id) {
        this.selectedFolder = undefined;
      } else {
        this.selectedFolder = folderItem;
      }
    }
  }

  navigateFolder(folderId: number) {
    this.folderItemRes = undefined;
    this.selectedFolder = undefined;
    this.filters = { folder: folderId || '' };
    if (folderId && this.layOutView !== 'list') {
      this.selectLayoutView('list');
    } else if (!folderId && this.layOutView !== 'grid_view') {
      this.selectLayoutView('grid_view');
    } else {
      this.onFilterChange();
    }
  }

  selectLayoutView(viewType: ViewType) {
    this.layOutView = viewType;
    this.pagination = {
      page: 1,
      page_size: 20,
    };
    this.onFilterChange();
  }

  onFilterChange(): void {
    this.pagination.page = 1;
    this.getFolderDetail();
    this.getList();
  }

  getList(): void {
    this.spinner.show();
    this.loading = true;
    this.service
      .getFolderItemList(this.queryParams)
      .pipe(
        finalize(() => {
          this.spinner.hide();
          this.loading = false;
        }),
      )
      .subscribe({
        next: (res: DrfList<FolderItemList>) => {
          this.folderItemRes = res;
          this.folderItemRes.results = this.backupList.concat(
            res.results,
          );
          this.backupList = [];
        },
        error: (error) => {
          this.alert.error(error.status);
        },
      });
  }

  getFolderDetail(): void {
    if (this.filters?.folder) {
      this.service
        .getFolderDetail(this.filters.folder, {
          permission: this.filterWithPermission,
        })
        .subscribe({
          next: (res: FolderDetail) => {
            this.currentFolderDetail = res;
          },
          error: (error) => {
            this.alert.error(error.status);
          },
        });
    } else {
      this.currentFolderDetail = undefined;
    }
  }

  changePage(e: number): void {
    this.pagination.page = e;
    this.getFolderDetail();
    this.getList();
  }

  loadMore() {
    if (this.folderItemRes?.next && !this.loading) {
      this.backupList = this.folderItemRes?.results || [];
      this.changePage(this.pagination.page + 1);
    }
  }

  closeModal() {
    this.filters = {};
    this.pagination = {
      page: 1,
      page_size: 20,
    };
    this.layOutView = 'grid_view';
    this.currentFolderDetail = undefined;
    this.folderItemRes = undefined;
    this.selectedFolder = undefined;
    this.modal.close();
  }

  confirm() {
    if (this.selectedFolder) {
      this.navigateFolder(this.selectedFolder.item_detail.id);
    } else {
      this.mode === 'moveToFolder'
        ? this.moveToFolder()
        : this.addToFolder();
    }
  }

  moveToFolder() {
    this.loading = true;
    let target_folder_id: number | null = null;
    if (this.currentFolderDetail) {
      target_folder_id = (this.currentFolderDetail as FolderDetail)
        .id;
    }
    this.service
      .moveToFolder(this.itemId, {
        folder: target_folder_id,
      })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: () => {
          this.alert.success();
          const queryParams = this.queryParams;
          this.closeModal();
          this.router.navigate(['/folders'], {
            queryParams: queryParams,
          });
        },
        error: (error) => {
          this.store.dispatch(new ErrorNotification(error));
        },
      });
  }

  addToFolder() {
    this.loading = true;
    let target_folder_id: number | null = null;
    if (this.currentFolderDetail) {
      target_folder_id = (this.currentFolderDetail as FolderDetail)
        .id;
    }
    this.memoService
      .addToFolder(this.itemId, {
        folder: target_folder_id,
      })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: () => {
          this.alert.success();
          const queryParams = this.queryParams;
          this.closeModal();
          this.router.navigate(['/folders'], {
            queryParams: queryParams,
          });
        },
        error: (error) => {
          this.store.dispatch(new ErrorNotification(error));
        },
      });
  }

  get queryParams(): Params {
    return {
      ...this.filters,
      ...this.pagination,
      permission: this.filterWithPermission,
      ordering: '-item_updated_at',
      listView: this.layOutView,
    };
  }

  get isDisableFolder(): boolean {
    return (
      this.isMissingTargetFolder ||
      (!!this.currentFolderDetail &&
        this.disabledFolders.includes(this.currentFolderDetail.id))
    );
  }

  get isMissingTargetFolder(): boolean {
    return (
      this.mode === 'addToFolder' &&
      !this.selectedFolder &&
      !this.currentFolderDetail
    );
  }

  get hasNoFolderToSelect(): boolean {
    return (
      this.mode === 'addToFolder' &&
      !this.filters?.folder &&
      !this.folderItemRes?.count
    );
  }

  get deviceType(): DeviceType {
    return this.themeService.getDeviceTypeByBreakpoint();
  }
}
